import React, { FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { submitTicket } from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { ValidationError } from '../admin/components/ValidationError';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';
import { useFrontendAuth } from '../hooks/useAuth';

const OrderIssueForm: React.FC = () => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const [searchParams] = useSearchParams();
    const orderIdParam = searchParams.get('orderId');
    const orderId = orderIdParam ? Number(orderIdParam) : undefined;
    const { user } = useFrontendAuth();

    const [subject, setSubject] = useState(
        orderId ? `Issue with order #${orderId}` : ''
    );
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!subject.trim() || !message.trim()) {
            setError(tr('order.issue.required', 'Subject and message are required.'));
            scrollToFirstErrorField();
            return;
        }
        setSubmitting(true);
        try {
            await submitTicket({
                orderId: orderId && !Number.isNaN(orderId) ? orderId : undefined,
                subject: subject.trim(),
                message: message.trim(),
            });
            setSuccess(true);
            setMessage('');
        } catch (err: unknown) {
            let msg = tr('order.issue.error', 'Could not submit. Please try again.');
            if (err && typeof err === 'object' && 'response' in err) {
                const data = (err as { response?: { data?: { message?: string } } }).response?.data;
                if (data?.message) msg = data.message;
            }
            setError(msg);
            scrollToFirstErrorField();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mx-auto max-w-xl py-6 sm:py-10">
            <Link
                to={orderId ? `/orders/${orderId}` : '/helpdesk'}
                className="mb-6 inline-flex min-h-11 items-center gap-1 text-sm text-[var(--accent-color)] hover:underline"
            >
                <ChevronLeft className="h-4 w-4" />
                {tr('common.back', 'Back')}
            </Link>

            <h1 className="mb-2 text-2xl font-bold text-[var(--text-color)]">
                {tr('order.issue.title', 'Report an order issue')}
            </h1>
            <p className="mb-6 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                {user?.email
                    ? tr('order.issue.signedin', 'Signed in as {email}').replace('{email}', user.email)
                    : ''}
            </p>

            {success ? (
                <div className="rounded-2xl border border-[var(--accent-color)]/40 bg-[color-mix(in_srgb,var(--accent-color),transparent_90%)] p-6 text-center">
                    <p className="font-medium text-[var(--text-color)]">
                        {tr('order.issue.success', 'Thanks — our team will get back to you soon.')}
                    </p>
                    <Link
                        to="/my-account/orders"
                        className="mt-4 inline-block text-sm text-[var(--accent-color)] underline"
                    >
                        {tr('account.myorders', 'My orders')}
                    </Link>
                </div>
            ) : (
                <form
                    onSubmit={handleSubmit}
                    className="space-y-4 rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_60%)] bg-[color-mix(in_srgb,var(--bg-color),white_15%)] p-6 shadow-sm"
                >
                    <div>
                        <label className="mb-1 block text-sm font-medium text-[var(--text-color)]">
                            {tr('order.issue.orderid', 'Order ID')}
                        </label>
                        <input
                            type="text"
                            readOnly
                            value={orderId && !Number.isNaN(orderId) ? String(orderId) : '—'}
                            className="w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] px-3 py-2.5 text-sm text-[var(--text-color)]"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-[var(--text-color)]">
                            {tr('order.issue.subject', 'Subject')}
                        </label>
                        <input
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            maxLength={200}
                            required
                            className="w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-white/80 px-3 py-2.5 text-sm text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]/40"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-[var(--text-color)]">
                            {tr('order.issue.message', 'Describe the issue')}
                        </label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={5}
                            maxLength={4000}
                            required
                            className="w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-white/80 px-3 py-2.5 text-sm text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]/40"
                        />
                    </div>
                    <ValidationError error={error ?? undefined} />
                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full rounded-xl bg-[var(--accent-color)] py-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    >
                        {submitting
                            ? '...'
                            : tr('order.issue.submit', 'Send to support')}
                    </button>
                </form>
            )}
        </div>
    );
};

export default OrderIssueForm;
